import { Injectable, inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { tap, shareReplay, finalize, switchMap, catchError } from 'rxjs/operators';
import { ApiService } from './api.service';
import { environment } from '../../../environments/environment';
import { DeckCanvas, RESPONSIVE_DECK_CANVAS } from './deck-canvas';

// Re-exported so the call page keeps importing the deck contract from here.
export type { DeckCanvas };

export interface ChatbotTheme {
  primaryText: string;
  primaryBg: string;
  primaryBorder: string;
  iconGradient: string;
  markdownStrong: string;
  markdownCode: string;
  markdownLink: string;
  accentText: string;
  accentBg: string;
  accentHover: string;
  accentBorder: string;
  accentFocus: string;
}

/**
 * The public demo's brand palette — the `--cd-*` custom properties the demo pages set on their host.
 *
 * Distinct from {@link ChatbotTheme}, which carries Tailwind class strings for the chatbot: these are
 * raw hexes, and four of them (`bgStart`/`bgEnd`, the `cta*` set) have no ChatbotTheme equivalent at
 * all, which is why they used to live in a hardcoded `if (productId === 'practina')` in four separate
 * files. Products without a `demoTheme` still work — {@link applyDemoTheme} derives one from
 * `chatbotTheme` and falls back to Credee's.
 *
 * Every field is REQUIRED. The demo SCSS declares no `:root` fallback for most of these, so an unset
 * custom property resolves to guaranteed-invalid and paints transparent — a partial theme is worse
 * than none. {@link resolveDemoTheme} therefore always returns a complete object.
 */
export interface DemoTheme {
  brand: string;
  brand600: string;
  brand50: string;
  brandLine: string;
  gradientStart: string;
  gradientEnd: string;
  /** Page wash behind the setup/landing hero. */
  bgStart: string;
  bgEnd: string;
  ctaBg: string;
  ctaHover: string;
  ctaColor: string;
  ctaDot: string;
  /** Text-wordmark lockup (the loader): the leading letter and the trailing period. */
  logoAccent: string;
  /** Text-wordmark lockup: the body of the word. */
  logoBody: string;
}

/** Per-product config for the shared Tracking Demo dashboard (live AI demo monitoring). */
export interface TrackingDemoProductConfig {
  /**
   * The backend's products.keyname (same value as the demo productToken), sent as the
   * `product=` query param on every live-call tracking API call to scope the dashboard.
   */
  productKey: string;
  /**
   * Public demo call page the covert observer JOIN opens (may carry a query, e.g.
   * `?token=practina` — the call page sends it as `productToken`). Absent → Join hidden.
   */
  observerCallPath?: string;
  /**
   * localStorage key prefix of the one-shot observer handoff — MUST match what the
   * product's call page reads. credee-demo-call.component reads
   * `credeeObserverHandoff:<nonce>` for EVERY product (it is the shared demo page).
   */
  observerHandoffKeyPrefix?: string;
}

export interface ProductStatItem {
  value: string;
  label: string;
}

export interface ProductFeatureItem {
  title: string;
  description?: string;
  icon?: 'mic' | 'monitor' | 'check' | 'video' | 'terminal' | 'shield' | 'zap';
}

export interface ProductLandingConfig {
  eyebrow?: string;
  titlePattern?: string;
  /**
   * Optional substring of the (resolved) title rendered in the brand accent
   * color. Must appear verbatim in the title or it is ignored.
   */
  titleAccent?: string;
  stats?: ProductStatItem[];
  subCaption?: string;
  featuresTitle?: string;
  features?: ProductFeatureItem[];
}

/**
 * The booking backends the consultant modal can schedule against — one per brand that runs a
 * scheduler, NOT one per product. Adding a product that books demos means adding its backend here
 * and a branch in consultant-scheduler.service.ts; pointing an existing product at one of these is
 * then just its {@link ProductConfig.consultantScheduler} field.
 */
export type ConsultantSchedulerProduct = 'credee' | 'practina' | 'denefits';

/**
 * How the call's stage cards (the "Your numbers" Pathways panel, the customer-portal card, the
 * metric and knowledge cards) render for one product. Every field is optional and every absent
 * field keeps today's Credee rendering, so only a product that opts in changes.
 */
export interface DemoCardsConfig {
  /** Paint the Pathways panel from the product's `--cd-*` theme instead of the fixed Credee-teal
   *  slide palette it was designed with. */
  themedPathways?: boolean;
  /** Second colour of the card accents (metric bars, knowledge-fact dots, the Pathways "Instant"
   *  pill). Absent → Credee's amber. */
  accent?: string;
  /** false → the Pathways "Upfront Financing" card never prints the standard deduction %
   *  (a product whose Financing rate is an approval outcome, never quoted). */
  quoteFinancingRate?: boolean;
  /** The customer-portal card's labels and copy. The tab KEYS stay the show_patient_portal tool's
   *  ('overview' | 'pay_in_full' | 'change_date'); only what the prospect reads changes. */
  portal?: {
    /** false → no Pay-in-full/Pay-off tab at all; a show_patient_portal call for
     *  'pay_in_full' lands on Overview instead. Default true. */
    showPayoff?: boolean;
    payoffTab?: string;
    payoffCopy?: string;
    payoffCta?: string;
    changeDateTab: string;
    changeDateCopy: string;
    overviewNote: string;
  };
}

export interface ProductConfig {
  id: string;
  displayName: string;
  productServerName: string;
  requiresAccessToken: boolean;
  payloadExtras?: Record<string, any>;
  hasAiFeatures?: boolean;
  reportToolsPath?: string;
  overdue_cron_sub_url?: string;
  overdue_cron_latest_sub_url?: string;
  abrivatedName?: string;
  chatbotTheme?: ChatbotTheme;
  termsUrl?: string;
  privacyUrl?: string;
  /** Route the guest-invite link targets. Absent → the Credee default slug, which
   *  lives on the EMBEDDING site only; a product with no such page must name this
   *  app's own registered route ('/instant-demo/call') instead. */
  inviteLinkRoute?: string;
  /** Per-embed-host overrides for {@link inviteLinkRoute}, keyed by lowercase hostname.
   *  For a product whose sites run DIFFERENT slugs at the same time — Credee's
   *  beta.credee.com still serves the legacy /credee-demo-invite while credee.com and
   *  dev.credee.com serve /ai-demo-invite — the build flag cannot pick the slug (both
   *  hosts are served by the one production build), so the resolved embed origin does.
   *  A host with no entry falls back to inviteLinkRoute. Resolved by
   *  {@link ProductConfigService.getInviteLinkRoute}. */
  inviteLinkRouteByHost?: Record<string, string>;
  /** Every hostname (lowercase, no scheme) this product's public sites are served on
   *  — the marketing site, its dev/beta hosts, and any app host the demo links out to.
   *  Lets a demo that arrives with no usable `?token=` recover its product from the
   *  embedding origin instead of silently defaulting to Credee. Resolved by
   *  {@link ProductConfigService.getProductTokenByHost}; a product with no entry here
   *  simply cannot be recovered that way. */
  embedHosts?: string[];
  signupUrl?: string;
  /** Origin that share/invite links are built on — the embedding product's site.
   *  An explicit '' means STANDALONE: links stay on this app's own origin. That is
   *  distinct from leaving the field out, which falls back to environment
   *  .credeeWeb.baseUrl (see the `??` in credee-demo-{call,setup}.component.ts). */
  embedBaseUrl?: string;
  supportEmail?: string;
  /** Support phone shown on the OpenAI engine's team card; omitted = no phone line. */
  supportPhone?: string;
  /** One line under the support email on the team card (e.g. in-app chat route). */
  supportChatHint?: string;
  /** One line on the OpenAI engine's getting-started card (compliance copy). */
  signupCardLine?: string;
  trackingDemo?: TrackingDemoProductConfig;
  demoSetupDescription?: string;
  landingConfig?: ProductLandingConfig;
  /** Display name for the AI agent persona shown in the demo UI (e.g. 'Alex', 'Ava'). */
  aiName?: string;
  /** Demo palette. Absent → derived from {@link ChatbotTheme} by {@link resolveDemoTheme}. */
  demoTheme?: DemoTheme;
  /** Per-product styling and copy for the call's stage cards. Absent → the Credee cards, unchanged. */
  demoCards?: DemoCardsConfig;
  /**
   * Partial override applied ONLY on the device-preferences screen, which has always run a calmer
   * palette than setup/call (deep teal CTA rather than amber). Transitional: it exists so collapsing
   * the four duplicated theme blocks into one helper is provably pixel-identical. Delete once design
   * signs off on a single palette per product — new products should not get one.
   */
  demoThemePreferences?: Partial<DemoTheme>;
  /** Tab icon for the demo. Absent → the built-in Credee mark. */
  faviconUrl?: string;
  /**
   * Loader treatment while the demo boots. Absent → the CSS bouncing-dot wordmark.
   * `gif` points at a pre-rendered animation instead.
   */
  demoLoader?: { kind: 'gif'; src: string } | { kind: 'wordmark' };
  /**
   * How to set the product name as a text lockup. `lower` is Credee's all-lowercase wordmark
   * ("credee."); `title` capitalises the initial ("Denefits."); `plain` skips the wordmark treatment
   * altogether and just sets the name, for a product whose real lockup is an image. Defaults to
   * `lower`.
   */
  wordmarkCase?: 'lower' | 'title' | 'plain';
  /**
   * Whose booking backend the "Connect with Consultant" scheduler posts to. Absent → this product has
   * no scheduler and the button rings the AI agent (`askAgent`) instead.
   *
   * Usually the product's own id, but not necessarily: it names the backend, so a product that books
   * through a sibling brand's scheduler points at that brand. The value must be one
   * consultant-scheduler.service.ts has a branch for — see {@link ConsultantSchedulerProduct}.
   */
  consultantScheduler?: ConsultantSchedulerProduct;
  /**
   * On the OpenAI engine, the consultant button normally opens the team contact card
   * instead of the live-rep modal (spec 2026-09-21). Set true to keep the modal there too
   * (Credee decision 2) — see {@link consultantButtonForEngine} in live-session-factory.ts.
   */
  consultantModal?: boolean;
  /**
   * Logical canvases the product's DECK slides (the pack pages shown in the demo's
   * deck iframe) are authored for, per shell. A declared canvas makes the call page
   * render the iframe at exactly that size and scale it uniformly into the deck box
   * (`--cd-deck-aspect` + `cd-deck-portrait` host class + `deckCanvasScale`; see
   * credee-demo-call.component.ts), so the document's own media queries see the
   * authored viewport instead of the phone's. A shell with no canvas keeps the
   * fluid landscape 16:10 letterbox the deck viewport has always used (Credee /
   * Practina slides are 16:9 vw-based; Lendee's desktop layout is the same).
   *
   * Lendee: its slides switch to a portrait layout under `(max-width:899.98px)` that is
   * set in fixed pixels for a ~460px phone frame twice as tall as wide. Letting it
   * reflow into a 285px-wide fluid iframe cramped it, and a landscape box clipped it
   * to its top third (field reports 2026-09-08). The same breakpoint also flips the
   * DESKTOP shell's fluid box (~876px on a 1366 laptop) into the phone layout, so the
   * desktop shell declares a landscape canvas too (2026-09-10).
   *
   * The keys are the canvases' SHAPES, not strictly the shells: `mobile` is the
   * portrait canvas (mobile shell, portrait viewport) and `desktop` the landscape one
   * (the desktop shell, and the mobile shell whenever the viewport is landscape — a
   * phone or tablet turned sideways; see deckCanvas() in the call component).
   */
  deckCanvas?: { desktop?: DeckCanvas; mobile?: DeckCanvas };
}

/**
 * Credee's canonical demo palette — the setup/call values, which are the ones the live landing page
 * renders. `brand`/`brand600` are the same `#007396`/`#005F7D` the chatbot uses, so there is one Credee
 * teal across the product. `brandLine` is pinned to the light teal that `--s2-brand-line` falls back to
 * in livekit-demo.scss (which the setup screen also loads), keeping that surface unchanged now that the
 * var is always set. `logo*` are the loader wordmark's amber-on-deep-teal, which renders outside
 * `.cd-root` and so used to reach only its hardcoded defaults.
 */
const CREDEE_DEMO_THEME: DemoTheme = {
  brand: '#007396',
  brand600: '#005F7D',
  brand50: '#0073961A',
  brandLine: '#b8dce6',
  gradientStart: '#006585',
  gradientEnd: '#0084ac',
  bgStart: '#fff7dc',
  bgEnd: '#dff1fa',
  ctaBg: '#ffca4d',
  ctaHover: '#f5bb2e',
  ctaColor: '#0c1017',
  ctaDot: '#007396',
  logoAccent: '#f5a623',
  logoBody: '#0e5a6e',
};

const PRACTINA_DEMO_THEME: DemoTheme = {
  brand: '#7101ff',
  brand600: '#5e00d6',
  brand50: '#7101ff14',
  brandLine: '#7101ff33',
  gradientStart: '#1d0041',
  gradientEnd: '#300753',
  bgStart: '#f4edff',
  bgEnd: '#e8dcff',
  ctaBg: '#7101ff',
  ctaHover: '#5e00d6',
  ctaColor: '#ffffff',
  ctaDot: '#ffffff',
  logoAccent: '#7101ff',
  logoBody: '#1d0041',
};

const DENEFITS_DEMO_THEME: DemoTheme = {
  brand: '#4482E2',
  brand600: '#357ABD',
  brand50: '#4482E214',
  brandLine: '#4482E233',
  gradientStart: '#1A3050',
  gradientEnd: '#2B4E7E',
  bgStart: '#EEF4FD',
  bgEnd: '#DFEAFB',
  ctaBg: '#4482E2',
  ctaHover: '#357ABD',
  ctaColor: '#FFFFFF',
  ctaDot: '#FFFFFF',
  // Navy "D" and period against the blue body — the inverse of Credee's amber-on-teal.
  logoAccent: '#1A3050',
  logoBody: '#4482E2',
};

/**
 * Lendee's green. `brand`/`brand600`/`cta*` are exactly what {@link resolveDemoTheme} would derive from
 * its `chatbotTheme`; the gradient and page wash are NOT — they were hand-picked (deep green into the
 * brand green, over a mint wash) and the derivation would flatten both to a single flat green. Spelled
 * out here so that hand-tuning survives.
 */
const LENDEE_DEMO_THEME: DemoTheme = {
  brand: '#049F5E',
  brand600: '#037E4B',
  brand50: '#049F5E14',
  brandLine: '#049F5E33',
  gradientStart: '#02593A',
  gradientEnd: '#049F5E',
  bgStart: '#EAFAF2',
  bgEnd: '#D9F4E7',
  ctaBg: '#049F5E',
  ctaHover: '#037E4B',
  ctaColor: '#FFFFFF',
  ctaDot: '#FFFFFF',
  logoAccent: '#049F5E',
  logoBody: '#02593A',
};

/**
 * Official Lendee mark (rounded square + "L"), per lendee.com/images/lendee_logo.svg. Inlined as a data
 * URI rather than pointed at a host: Lendee publishes no stable favicon URL. Sits here with the other
 * per-product branding now that credee-favicon.ts resolves the icon from `faviconUrl` alone.
 */
const LENDEE_FAVICON =
  'data:image/svg+xml,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36.3567 36.6751" fill="none">' +
    '<path d="M0 4.86507C0 2.17817 2.18151 0 4.87255 0H36.3567V31.8101C36.3567 34.4969 34.1751 36.6751 31.4841 36.6751H4.87255C2.18151 36.6751 0 34.4969 0 31.8101V4.86507Z" fill="url(#ldfav)"/>' +
    '<path d="M7.68555 5.42578H8.34147C12.3707 5.70646 13.8699 9.33417 13.8699 11.0393V31.6223H13.4951C9.84071 31.4352 7.68555 28.9091 7.68555 26.4765V5.42578Z" fill="white"/>' +
    '<path d="M29.0471 25.4492V26.1041C28.766 30.1272 25.1327 31.6241 23.4249 31.6241H13.8672V31.2499V25.4492H29.0471Z" fill="url(#ldfavfoot)"/>' +
    '<defs><linearGradient id="ldfav" x1="34.4826" y1="36.6751" x2="8.01262" y2="5.01216" gradientUnits="userSpaceOnUse">' +
    '<stop stop-color="#005B41"/><stop offset="1" stop-color="#00844C"/>' +
    '</linearGradient>' +
    '<linearGradient id="ldfavfoot" x1="28.4848" y1="28.5367" x2="13.8672" y2="28.5367" gradientUnits="userSpaceOnUse">' +
    '<stop stop-color="white"/><stop offset="1" stop-color="#78A896"/>' +
    '</linearGradient></defs>' +
    '</svg>',
  );

/**
 * The demo palette for a product: an explicit {@link DemoTheme} when it has one, otherwise a coherent
 * single-hue theme derived from its `chatbotTheme` — so a product with no demo block renders in its OWN
 * colour rather than inheriting Credee's teal-and-amber. Never returns a partial object.
 */
export function resolveDemoTheme(config: ProductConfig | null | undefined): DemoTheme {
  if (config?.demoTheme) return config.demoTheme;

  const chatbot = config?.chatbotTheme;
  const brand = chatbot?.markdownStrong || CREDEE_DEMO_THEME.brand;
  const brand600 = chatbot?.accentHover?.match(/\[(.*?)\]/)?.[1] || brand;
  const gradient = chatbot?.iconGradient ?? '';
  const gradientStart = gradient.match(/from-\[(#[0-9a-fA-F]+)\]/)?.[1] ?? brand;
  const gradientEnd = gradient.match(/to-\[(#[0-9a-fA-F]+)\]/)?.[1] ?? brand;

  return {
    brand,
    brand600,
    brand50: brand + '1A',
    brandLine: brand + '33',
    gradientStart,
    gradientEnd,
    bgStart: brand + '0F',
    bgEnd: brand + '08',
    ctaBg: brand,
    ctaHover: brand600,
    ctaColor: '#ffffff',
    ctaDot: '#ffffff',
    logoAccent: brand,
    logoBody: gradientEnd,
  };
}

const PRODUCT_CONFIGS: Record<string, ProductConfig> = {
  'denefits': {
    id: 'denefits',
    displayName: 'Denefits',
    productServerName: 'denefits',
    requiresAccessToken: false,
    aiName: 'Dena',
    // Live-call tracking dashboard scope. Without this block the panel inherits
    // Credee's and every tracking API call goes out as `product=credee`.
    trackingDemo: {
      productKey: 'denefits',
      observerCallPath: '/instant-demo/call?token=denefits',
      observerHandoffKeyPrefix: 'credeeObserverHandoff',
    },
    hasAiFeatures: true,
    // Denefits' own admin scheduler (book_training_for_guest), not Credee's training module.
    consultantScheduler: 'denefits',
    reportToolsPath: 'visionboard/tools',
    overdue_cron_sub_url: 'subadmin/get_friday_cron_data',
    overdue_cron_latest_sub_url: 'subadmin/get_latest_friday_cron_data',
    demoTheme: DENEFITS_DEMO_THEME,
    // Denefits' own stage cards (2026-09-28): the Pathways panel and card accents take the
    // Denefits blue/navy instead of Credee's teal/amber; the Financing card never prints a rate
    // (Denefits sign-off: the rate is an approval outcome); the portal card shows Overview and
    // Change due date only — no Pay-in-full/Pay-off tab on Denefits (product direction 2026-09-28).
    demoCards: {
      themedPathways: true,
      accent: '#1A3050',
      quoteFinancingRate: false,
      portal: {
        showPayoff: false,
        changeDateTab: 'Change due date',
        changeDateCopy: 'Life happens. Move a payment due date yourself. A small change-date fee may apply.',
        overviewNote: 'Self-serve: partial payments, due-date changes and payment history. No front-desk call.',
      },
    },
    faviconUrl: 'https://www.denefits.com/wp-content/uploads/2024/09/favicon.png',
    // The bouncing-dot wordmark, not the official SVG lockup: the loader wants motion.
    demoLoader: { kind: 'wordmark' },
    wordmarkCase: 'title',
    termsUrl: 'https://www.denefits.com/terms-and-conditions/',
    privacyUrl: 'https://www.denefits.com/privacy-policy/',
    supportEmail: 'support@denefits.com',
    // Served by the Denefits marketing site, not routed in this app — same arrangement as Practina.
    inviteLinkRoute: '/product-specialist-invite/',
    embedBaseUrl: environment.production ? 'https://www.denefits.com' : 'https://dev.denefits.com',
    // Single destination for both roles, matching Practina; Credee's role-split fallback does not apply.
    signupUrl: environment.production
      ? 'https://business.denefits.com/register'
      : 'https://testbusiness.denefits.com/register',
    demoSetupDescription: "Answer three quick questions and your Denefits specialist will walk you through a personalized demo: how the checkout and payment options work, the numbers for your business, and how to get started. The same walkthrough our specialists give, on demand.",
    landingConfig: {
      eyebrow: 'Live interactive demo',
      titlePattern: 'See what Denefits does to your checkout — live.',
      stats: [
        { value: '40,000+', label: 'businesses on the platform' },
        { value: '+38%', label: 'checkout lift' },
        { value: '11 min', label: 'average setup time' },
      ],
      featuresTitle: 'How Denefits helps you close more deals',
      features: [
        { title: 'Flexible payment options at checkout', icon: 'check' },
        { title: 'Protected payments & recovery', icon: 'check' },
        { title: 'Drag-and-drop checkout builder', icon: 'check' },
        { title: 'Send by SMS, email, QR or link', icon: 'check' },
      ],
    },
    chatbotTheme: {
      primaryText: 'text-[#4482E2]',
      primaryBg: 'bg-[#4482E2]/[0.08]',
      primaryBorder: 'border-[#4482E2]/[0.15]',
      iconGradient: 'from-[#4482E2] to-[#1A3050]',
      markdownStrong: '#4482E2',
      markdownCode: '#4482E2',
      markdownLink: '#4482E2',
      accentText: 'text-white',
      accentBg: 'bg-[#4482E2]',
      accentHover: 'hover:bg-[#357ABD]',
      accentBorder: 'border-[#357ABD]',
      accentFocus: 'focus-within:border-[#4482E2]/50'
    }
  },
  'credee': {
    id: 'credee',
    displayName: 'Credee',
    productServerName: 'credee',
    requiresAccessToken: true,
    aiName: 'Alex',
    consultantModal: true,
    // OpenAI engine's team + getting-started cards (spec 2026-09-21, Credee on OpenAI).
    // supportEmail equals the call page's own fallback, so the Gemini page is unchanged;
    // supportPhone / signupCardLine are read only by the OpenAI cards. NO signupUrl here on
    // purpose: the page's signupUrl() returns config.signupUrl BEFORE its role-based Credee
    // fallback, so setting one would change the Gemini page — the cards get the page's own
    // link through their `signupUrl` input instead. signupCardLine is compliance copy
    // (Credee product-owner sign-off) and makes no product claim.
    supportEmail: 'support@credee.com',
    supportPhone: '+1 888-404-1187',
    signupCardLine: 'Register your business with Credee online. The page opens in a new tab, so this demo keeps running.',
    // NOTE: the public demo's REGISTERED route is /instant-demo/call (app.routes.ts) —
    // the credee-demo folder/comments' "/credee-demo/call" path is NOT routed and
    // wildcard-bounces to '/'.
    trackingDemo: {
      productKey: 'credee',
      observerCallPath: '/instant-demo/call?token=credee',
      observerHandoffKeyPrefix: 'credeeObserverHandoff',
    },
    // Credee's embedding sites are on TWO different slugs at once (published active
    // routes, 2026-09-08): beta.credee.com keeps the legacy /credee-demo-invite (and
    // /instant-demo-invite), while credee.com and dev.credee.com serve /ai-demo-invite —
    // the slug every other product migrated to. One production build serves both hosts,
    // so the slug is resolved from the embed origin, not from environment.production
    // (which is what the old `/credee-demo-invite/` vs `/instant-demo-invite/` fallback
    // in credee-demo-call.component.ts did, and why dev minted a dead
    // dev.credee.com/instant-demo-invite/ link). No embedBaseUrl on purpose: the
    // environment.credeeWeb.baseUrl fallback (beta prod / dev.credee.com dev) stays the
    // default, and a credee.com wrapper passes ?baseUrl=https://credee.com.
    inviteLinkRoute: '/ai-demo-invite/',
    inviteLinkRouteByHost: {
      'beta.credee.com': '/credee-demo-invite/', // /ai-demo-invite is NOT published on beta
      'credee.com': '/ai-demo-invite',           // no trailing slash — matches the live route
    },
    embedHosts: ['credee.com', 'www.credee.com', 'beta.credee.com', 'dev.credee.com'],
    payloadExtras: {
      is_dummy: 0,
      timezone: 'PDT'
    },
    demoTheme: CREDEE_DEMO_THEME,
    // The preferences screen has always run a calmer, deeper teal with a solid CTA. Transitional —
    // see ProductConfig.demoThemePreferences.
    demoThemePreferences: {
      brand: '#0d6b7c',
      brand600: '#084d59',
      brand50: '#f0f7f8',
      brandLine: '#d5e6e9',
      gradientStart: '#092429',
      gradientEnd: '#0f3b43',
      bgStart: '#fffdf5',
      bgEnd: '#f0f7f9',
      ctaBg: '#0d6b7c',
      ctaHover: '#084d59',
      ctaColor: '#ffffff',
      ctaDot: '#0d6b7c',
    },
    consultantScheduler: 'credee',
    reportToolsPath: 'visionboard/tools',
    overdue_cron_sub_url: 'visionboard/tools/get_friday_cron_data',
    overdue_cron_latest_sub_url: 'visionboard/tools/get_latest_friday_cron_data',
    // Deck geometry (2026-09-15 responsive slide set): the slides are a self-sizing
    // 16:9 stage that letterboxes itself inside any landscape box, fills a short
    // landscape box (<=520px tall) and stacks in a portrait one, with px readability
    // floors — so they must render FLUID at the real viewport, never transform-scaled
    // (see DeckCanvas.fit). Desktop: shape the box 16:9 so the slide's own bands
    // disappear from the 16:10 default box. Mobile: no box at all — the frame is the
    // whole slides panel (the no-canvas default sized it off the iframe's intrinsic
    // 300px: a 302x190 strip in portrait, a height-bound 16:10 frame centred in a
    // wider panel in landscape — field screenshot 2026-09-15). The `desktop` entry
    // also serves a phone turned sideways (see deckCanvas() in the call component),
    // where the mobile shell's width-bound rule + max-height cap fills the panel.
    deckCanvas: RESPONSIVE_DECK_CANVAS,
    chatbotTheme: {
      primaryText: 'text-[#007396]',
      primaryBg: 'bg-[#007396]/[0.05]',
      primaryBorder: 'border-[#007396]/[0.1]',
      iconGradient: 'from-[#007396] to-[#FFC459]',
      markdownStrong: '#007396',
      markdownCode: '#007396',
      markdownLink: '#007396',
      accentText: 'text-white',
      accentBg: 'bg-[#007396]',
      accentHover: 'hover:bg-[#005F7D]',
      accentBorder: 'border-[#005F7D]',
      accentFocus: 'focus-within:border-[#007396]/50'
    },
    demoSetupDescription: "Answer three quick questions and your Credee specialist will walk you through a personalized demo: how the payment plans work, the numbers for your business, and how to get started. The same walkthrough our specialists give, on demand.",
    landingConfig: {
      eyebrow: 'Live interactive demo',
      titlePattern: 'Watch Credee turn a "maybe" into a paid sale — live.',
      stats: [
        { value: '97%', label: 'customer approval rate — no credit checks' },
        { value: '30,000+', label: 'businesses closing sales on Credee' },
        { value: '<60s', label: 'to secure a sale at checkout' },
      ],
      featuresTitle: 'How Credee helps you close more deals',
      features: [
        { title: 'Payment plans that close the deal', icon: 'check' },
        { title: 'Protected payments & auto-recovery', icon: 'check' },
        { title: 'Revenue Builder checkout', icon: 'check' },
        { title: 'Credee Terminal for faster checkouts', icon: 'check' },
      ],
    },
  },
  'lendee': {
    id: 'lendee',
    displayName: 'Lendee',
    productServerName: 'lendee',
    requiresAccessToken: false,
    // TODO(lendee-persona): placeholder pending the product team's pick — must stay in
    // step with the backend pack (call_agent/products/lendee/product.yaml agent.name).
    aiName: 'Lena',
    // Lendee demos run on the SAME /instant-demo pages and the SAME unified backend
    // mount (/live-call) as every product — `?token=lendee` rides as `productToken`,
    // and the observer handoff key is the shared page's `credeeObserverHandoff`.
    trackingDemo: {
      productKey: 'lendee',
      observerCallPath: '/instant-demo/call?token=lendee',
      observerHandoffKeyPrefix: 'credeeObserverHandoff',
    },
    demoTheme: LENDEE_DEMO_THEME,
    faviconUrl: LENDEE_FAVICON,
    // TODO(lendee-urls): confirm terms/privacy paths and the dev host once published.
    termsUrl: 'https://www.lendee.com/terms-conditions/',
    privacyUrl: 'https://www.lendee.com/privacy-policy/',
    // Sign-up lands on Lendee's own onboarding, not the marketing site: dev builds go
    // to the dev app host, production to the live app (2026-09-06). Both are spelled
    // out per environment in environment.lendeeWeb.signupUrl — the two hosts differ by
    // more than the subdomain (dev keeps a /user/ prefix), and neither is the
    // embedBaseUrl host below.
    signupUrl: environment.lendeeWeb.signupUrl,
    // Guest invites open Lendee's own invite page, which re-loads this demo in its
    // iframe — the same shape as practina below. The page lives on the lendeeapp.com
    // hosts (environment.lendeeWeb: www.lendeeapp.com live, dev.lendeeapp.com dev — the
    // only two in the published active routes, 2026-09-08), not the marketing site:
    // www.lendee.com never had it (404) and dev.lendee.com does not resolve (NXDOMAIN),
    // which is why this briefly fell back to our own /instant-demo/call route
    // (2026-09-06). Note that is NOT the app.lendee.com host signupUrl uses. The slug is
    // the same on both hosts, so it is pinned rather than left to the invite-route
    // default (which would mint /credee-demo-invite/ on production).
    inviteLinkRoute: '/ai-demo-invite/',
    embedBaseUrl: environment.lendeeWeb.baseUrl,
    // Both the lendeeapp.com demo hosts and the lendee.com marketing/app hosts: any of
    // them can be the parent page a token-less demo has to be recovered from.
    embedHosts: [
      'www.lendeeapp.com', 'dev.lendeeapp.com', 'lendeeapp.com',
      'app.lendee.com', 'www.lendee.com', 'lendee.com',
    ],
    supportEmail: 'support@lendee.com',
    supportChatHint: 'You can also use Chat in the app, or Settings, then Contact Us.',
    signupCardLine: 'Sign up with Google, Apple or your mobile number, and browse Receivables before adding any money.',
    // Both shells (2026-09-10 deck, 13 slides): the slides switch to their phone
    // composition under `(max-width:899.98px)` and keep a 16:9 landscape composition
    // from 900px up (a `(max-width:1199px)` band floors the type sizes). The fluid
    // 16:10 desktop box is only ~876px wide on a 1366 laptop, which put the PHONE
    // layout inside the landscape box and overflowed it — so desktop now lays the
    // frame out at a fixed 1056x594 (inside the 900-1199 band, so the floors apply;
    // the narrowest 16:9 canvas at which every slide, the five-tier ladder included,
    // clears its own clipped stage) and scales it into the box. Mobile stays the
    // 460-wide, 2:1-tall phone frame. See ProductConfig.deckCanvas.
    deckCanvas: { desktop: { width: 1056, height: 594 }, mobile: { width: 460, height: 920 } },
    demoSetupDescription: "Explore how Lendee lets you purchase portions of verified business receivables and earn as scheduled payments come in. See the process, the numbers, and how Lendee manages everything in between.",
    // Stats adopted verbatim from the approved 2026-09 landing mock
    // (demo-panel-standalone.html), per product decision 2026-09-02. Note the
    // margin/earnings figures are performance numbers — keep them in sync with
    // what the Lendee KB actually certifies.
    landingConfig: {
      eyebrow: 'Live interactive demo',
      titlePattern: 'See how Lendee puts your money to work — live.',
      titleAccent: 'work',
      stats: [
        { value: '97%', label: 'receivables paid on schedule' },
        { value: '25%', label: 'average margin earned' },
        { value: '$568', label: 'approx. average earnings' },
      ],
      featuresTitle: 'How Lendee puts your Money to work',
      features: [
        { title: 'Access verified business receivables' },
        { title: 'Choose how much you want to purchase' },
        { title: 'Track payments as they come in' },
        { title: 'Let Lendee handle the servicing' },
      ],
    },
    chatbotTheme: {
      primaryText: 'text-[#049F5E]',
      primaryBg: 'bg-[#049F5E]/[0.08]',
      primaryBorder: 'border-[#049F5E]/[0.15]',
      iconGradient: 'from-[#049F5E] to-[#049F5E]/[0.7]',
      markdownStrong: '#049F5E',
      markdownCode: '#049F5E',
      markdownLink: '#049F5E',
      accentText: 'text-white',
      accentBg: 'bg-[#049F5E]',
      accentHover: 'hover:bg-[#037E4B]',
      accentBorder: 'border-[#037E4B]',
      accentFocus: 'focus-within:border-[#049F5E]/50'
    }
  },
  'cool credit': {
    id: 'cool-credit',
    displayName: 'Cool Credit',
    productServerName: 'cool-credit',
    requiresAccessToken: false,
    chatbotTheme: {
      primaryText: 'text-[#049F5E]',
      primaryBg: 'bg-[#049F5E]/[0.08]',
      primaryBorder: 'border-[#049F5E]/[0.15]',
      iconGradient: 'from-[#049F5E] to-[#049F5E]/[0.7]',
      markdownStrong: '#049F5E',
      markdownCode: '#049F5E',
      markdownLink: '#049F5E',
      accentText: 'text-white',
      accentBg: 'bg-[#049F5E]',
      accentHover: 'hover:bg-[#037E4B]',
      accentBorder: 'border-[#037E4B]',
      accentFocus: 'focus-within:border-[#049F5E]/50'
    }
  },
  'finance mutual': {
    id: 'finance_mutual',
    displayName: 'Finance Mutual',
    productServerName: 'finance-mutual',
    abrivatedName: 'fm',
    requiresAccessToken: false,
    reportToolsPath: 'visionboard',
    overdue_cron_sub_url: 'visionboard/get_friday_cron_data',
    overdue_cron_latest_sub_url: 'visionboard/get_latest_friday_cron_data',
    chatbotTheme: {
      primaryText: 'text-[#004647] dark:text-[#EBD397]',
      primaryBg: 'bg-[#EBD397]/[0.2]',
      primaryBorder: 'border-[#EBD397]',
      iconGradient: 'from-[#004647] to-[#EBD397]',
      markdownStrong: '#004647',
      markdownCode: '#004647',
      markdownLink: '#004647',
      accentText: 'text-white',
      accentBg: 'bg-[#004647]',
      accentHover: 'hover:bg-[#003031]',
      accentBorder: 'border-[#003031]',
      accentFocus: 'focus-within:border-[#004647]/50'
    }
  },
  'recuvery': {
    id: 'recuvery',
    displayName: 'Recuvery',
    productServerName: 'recuvery',
    requiresAccessToken: false,
    reportToolsPath: 'visionboard',
    overdue_cron_sub_url: 'visionboard/get_friday_cron_data',
    overdue_cron_latest_sub_url: 'visionboard/get_latest_friday_cron_data',
    chatbotTheme: {
      primaryText: 'text-[#279F63]',
      primaryBg: 'bg-[#279F63]/[0.08]',
      primaryBorder: 'border-[#279F63]/[0.15]',
      iconGradient: 'from-[#279F63] to-[#279F63]/[0.7]',
      markdownStrong: '#279F63',
      markdownCode: '#279F63',
      markdownLink: '#279F63',
      accentText: 'text-white',
      accentBg: 'bg-[#279F63]',
      accentHover: 'hover:bg-[#1E7E4F]',
      accentBorder: 'border-[#1E7E4F]',
      accentFocus: 'focus-within:border-[#279F63]/50'
    }
  },
  'practina': {
    id: 'practina',
    displayName: 'Practina',
    productServerName: 'practina',
    requiresAccessToken: false,
    aiName: 'Ava',
    hasAiFeatures: true,
    // Practina demos run on the SAME /instant-demo pages and the SAME unified backend
    // mount (/live-call) as every product — `?token=practina` rides as `productToken`,
    // and the observer handoff key is the shared page's `credeeObserverHandoff`. Room
    // mutations and reads both hit live-call; the dashboard is scoped by `product=`.
    trackingDemo: {
      productKey: 'practina',
      observerCallPath: '/instant-demo/call?token=practina',
      observerHandoffKeyPrefix: 'credeeObserverHandoff',
    },
    // Deck geometry: Credee's responsive contract (deck-canvas.ts) — a fluid 16:9 box on
    // desktop and on a phone turned sideways, the whole slides panel in mobile portrait,
    // so the uploaded slides' own `@media (orientation: portrait)` rules see the real
    // device and stack. Without an entry the mobile shell sized the box off the
    // iframe's intrinsic 300px (a ~302x190 strip on phones). Slides that are not yet
    // orientation-aware letterbox a 16:9 strip inside the panel; a slide that switches
    // on `max-width` instead will reflow on phones and in the ~876px laptop box.
    deckCanvas: RESPONSIVE_DECK_CANVAS,
    demoTheme: PRACTINA_DEMO_THEME,
    // Transitional, as Credee's — see ProductConfig.demoThemePreferences.
    demoThemePreferences: {
      brand: '#6d28d9',
      brand600: '#5b21b6',
      brand50: '#f5f3ff',
      brandLine: '#e9d5ff',
      gradientStart: '#2e1065',
      gradientEnd: '#4c1d95',
      bgStart: '#faf5ff',
      bgEnd: '#f3e8ff',
      ctaBg: '#6d28d9',
      ctaHover: '#5b21b6',
      ctaColor: '#ffffff',
      ctaDot: '#ffffff',
    },
    faviconUrl: 'https://practina.com/wp-content/uploads/2024/03/site-icon-300x300.png',
    // Practina's lockup is its wordmark image; set plainly wherever only text is available.
    wordmarkCase: 'plain',
    demoLoader: { kind: 'gif', src: 'https://d3judojthu11zu.cloudfront.net/email-template-icons/prctina_loader.gif' },
    termsUrl: 'https://www.practina.com/terms-conditions/',
    privacyUrl: 'https://www.practina.com/privacy-policy/',
    // Same slug on prod and dev — the page lives on www.practina.com / dev.practina.com
    // (see embedBaseUrl below), never as a route in this app.
    inviteLinkRoute: '/ai-demo-invite/',
    // Practina's own scheduler_guest API, not Credee's training module.
    consultantScheduler: 'practina',
    // Per environment in environment.practinaWeb.signupUrl. Note it stays on the APEX
    // practina.com on live, unlike the www embedBaseUrl below — left exactly as it was.
    signupUrl: environment.practinaWeb.signupUrl,
    embedHosts: ['www.practina.com', 'practina.com', 'dev.practina.com', 'staging.practina.com'],
    // environment.practinaWeb is WWW on live, not the apex: the published active route
    // is www.practina.com/ai-demo-invite/ (2026-09-08). The apex only redirected there,
    // and syncUrlToParent pins its postMessage to THIS origin — an apex/www mismatch
    // silently killed the URL sync.
    embedBaseUrl: environment.practinaWeb.baseUrl,
    supportEmail: 'support@practina.com',
    chatbotTheme: {
      primaryText: 'text-[#7101FF]',
      primaryBg: 'bg-[#7101FF]/[0.08]',
      primaryBorder: 'border-[#7101FF]/[0.15]',
      iconGradient: 'from-[#7101FF] to-[#1D0041]',
      markdownStrong: '#7101FF',
      markdownCode: '#7101FF',
      markdownLink: '#7101FF',
      accentText: 'text-white',
      accentBg: 'bg-[#7101FF]',
      accentHover: 'hover:bg-[#5E00D6]',
      accentBorder: 'border-[#5E00D6]',
      accentFocus: 'focus-within:border-[#7101FF]/50'
    },
    demoSetupDescription: "Ready to see what Practina can do for you? Answer a few quick questions and our AI-powered specialist will walk you through the growth journey you can build with Practina. Right from getting customers through the door to getting paid and turning every payment experience into an opportunity to bring them back.",
    landingConfig: {
      eyebrow: 'Live interactive demo',
      titlePattern: 'See Practina in action: a demo built around your business.',
      stats: [
        { value: 'Flexible', label: 'Payment Options' },
        { value: '50+ Global', label: 'Payment Methods' },
        { value: 'Marketing', label: 'On Autopilot' },
      ],
      subCaption: 'Built for local service businesses — dental, medspa, healthcare, chiropractic, veterinary, home services, legal, beauty & wellness, credit repair, and more.',
      features: [
        {
          title: 'Guided in real time',
          description: 'A specialist walks you through Practina based on your business, goals, and what you want to improve.',
          icon: 'mic',
        },
        {
          title: 'See the product live',
          description: 'Explore the tools, payment options, and workflows your business can actually use.',
          icon: 'monitor',
        },
        {
          title: 'Get started on the spot',
          description: 'Ask questions, see how it works, and understand how Practina fits into your day-to-day.',
          icon: 'check',
        },
        {
          title: 'Your camera, your choice',
          description: "Join with video or keep your camera off. Either way, you're in control.",
          icon: 'video',
        },
      ],
    },
  },
};

@Injectable({
  providedIn: 'root'
})
export class ProductConfigService {
  private apiService = inject(ApiService);
  private refreshToken$: Observable<any> | null = null;

  constructor() { }

  /**
   * Returns all available product configurations.
   */
  getProductConfigs(): ProductConfig[] {
    return Object.values(PRODUCT_CONFIGS);
  }

  /**
   * Returns the AI agent's display name for the given product identifier.
   * Falls back to 'Alex' (the Credee default) if no aiName is configured.
   */
  getAiName(productIdentifier: string): string {
    const config = this.getProductConfig(productIdentifier);
    return config?.aiName || 'Alex';
  }

  /**
   * Resolves the product name from the product ID using sessionStorage 'dynamic_routes'
   * or returns the ID itself if not found.
   */
  getProductName(productId: string): string {
    const stored = sessionStorage.getItem('dynamic_routes');
    const dynamicRoutes = stored ? JSON.parse(stored) : [];
    const selectedProduct = dynamicRoutes.find((p: any) => (p.product?.id === productId || p.id === productId));
    const rawProductName = selectedProduct?.product?.name || '';

    return rawProductName || productId;
  }

  /** True when the selected product is Denefits (Ask Prism AI is Denefits-only). */
  isDenefitsProduct(productId: string | null | undefined): boolean {
    if (!productId) return false;
    return this.getProductName(productId).toLowerCase() === 'denefits';
  }

  /**
   * Retrieves the configuration for a given product name or ID.
   * Defaults to 'Denefits' if not found.
   *
   * NOTE: this NEVER returns null — an identifier it does not recognize silently
   * resolves to Denefits, so `getProductConfig(x)` alone cannot tell "this IS
   * Denefits" from "no idea what this is". Callers that brand a whole page off the
   * result must ask {@link hasProductConfig} first; see the demo call page, where
   * the unrecognized → Denefits → 'credee' collapse rebranded Practina sessions.
   */
  getProductConfig(productIdentifier: string): ProductConfig {
    return this.findProductConfig(productIdentifier) || PRODUCT_CONFIGS['denefits'];
  }

  /** True when the identifier names a real configured product, i.e. when
   *  {@link getProductConfig} is returning a match rather than its Denefits default. */
  hasProductConfig(productIdentifier: string | null | undefined): boolean {
    return !!productIdentifier && !!this.findProductConfig(productIdentifier);
  }

  /** The matching config, or undefined — {@link getProductConfig} without the default. */
  private findProductConfig(productIdentifier: string): ProductConfig | undefined {
    const name = productIdentifier?.toLowerCase().trim();
    if (!name) return undefined;

    // Direct match
    if (PRODUCT_CONFIGS[name]) {
      return PRODUCT_CONFIGS[name];
    }

    // Search by properties
    return Object.values(PRODUCT_CONFIGS).find(c =>
      c.id === name ||
      c.displayName.toLowerCase() === name ||
      c.productServerName.toLowerCase() === name ||
      c.productServerName.replace('-', '_') === name || // Handle cool-credit vs cool_credit
      c.id.replace('-', '_') === name
    );
  }

  /**
   * Product token (the backend keyname, which is also the demo's `?token=`) for the
   * site at `url`, or null when the host belongs to no configured product.
   *
   * This is how a demo recovers its product when the link arrives WITHOUT a usable
   * `?token=`: the embedding origin — `?baseUrl=`, else the iframe's referrer, which
   * is the parent page — names the product. Without it the token chain fell straight
   * through to 'credee', and a Practina session ran as Credee end to end: Credee's
   * signup URL on the CTA, theme, favicon, reviews and decks (field-reported
   * 2026-09-08). Matches on {@link ProductConfig.embedHosts}.
   */
  getProductTokenByHost(url: string | null | undefined): string | null {
    if (!url) return null;
    let host: string;
    try {
      host = new URL(url).hostname.toLowerCase();
    } catch {
      return null; // not an absolute URL (an empty referrer, a bare path) — no signal
    }
    if (!host) return null;
    const match = Object.values(PRODUCT_CONFIGS).find(c => c.embedHosts?.includes(host));
    return match?.productServerName ?? null;
  }

  /**
   * Invite-page path for this product on the given embed origin: a host listed in
   * {@link ProductConfig.inviteLinkRouteByHost} wins, otherwise the product's plain
   * {@link ProductConfig.inviteLinkRoute}. Returns undefined when the product declares
   * neither, leaving the caller's own fallback in charge. The host lookup exists for
   * Credee, whose beta and live sites serve different invite slugs off ONE production
   * build (see the credee entry above) — a non-URL or empty base just takes the default.
   */
  getInviteLinkRoute(productIdentifier: string, embedBaseUrl?: string | null): string | undefined {
    const config = this.getProductConfig(productIdentifier);
    const byHost = config?.inviteLinkRouteByHost;
    if (byHost && embedBaseUrl) {
      try {
        const route = byHost[new URL(embedBaseUrl).hostname.toLowerCase()];
        if (route) return route;
      } catch { /* not an absolute URL — fall through to the default */ }
    }
    return config?.inviteLinkRoute;
  }

  /**
   * Tracking-dashboard config for a product NAME from route data / dynamic_routes
   * (e.g. "Practina"). An explicit `trackingDemo` block wins; every other product
   * gets a derived config, because ALL products demo on the same /instant-demo
   * pages and the same unified /live-call backend: the backend keyname is the
   * product's `productServerName` with `-` → `_` (cool-credit → cool_credit), the
   * observer join opens the shared call page with `?token=<keyname>`, and the
   * handoff key is the shared page's `credeeObserverHandoff`. A product absent
   * from PRODUCT_CONFIGS entirely falls back to Credee (the pre-productization
   * default) rather than silently showing another product's data.
   */
  getTrackingDemoConfig(productName: string | null | undefined): TrackingDemoProductConfig {
    const cfg = productName ? this.getProductConfig(productName) : null;
    if (cfg?.trackingDemo) return cfg.trackingDemo;
    const key = (cfg?.productServerName ?? 'credee').replace(/-/g, '_');
    return {
      productKey: key,
      observerCallPath: `/instant-demo/call?token=${key}`,
      observerHandoffKeyPrefix: 'credeeObserverHandoff',
    };
  }

  /**
   * Generates a new refresh token for the specified product (implicitly by selected product ID).
   * Note: This method usually relies on the currently selected product context.
   * For the dashboard components, we often pass the product ID or name.
   * Here we allow passing the productId explicitly, or fallback to a default if needed.
   */
  generateNewRefreshToken(productId: string): Observable<any> {
    if (this.refreshToken$) {
      return this.refreshToken$;
    }

    const productName = this.getProductName(productId);
    const productConfig = this.getProductConfig(productName);

    this.refreshToken$ = this.apiService
      .post(
        '/visionboard/refresh_auth_token_with_jwt',
        {},
        {
          addAccessToken: true,
          productServerName: productConfig.productServerName,
          showErrorToaster: true,
          showSuccessToaster: false,
          showLoader: false,
          headers: { 'x-rotate-refresh-token': 'true' },
        }
      )
      .pipe(
        tap((res: any) => {
          if (res && res.data && res.data.access_token) {
            this.apiService.setProductAccessToken(
              productConfig.productServerName,
              res.data.access_token
            );
          }
        }),
        shareReplay(1),
        finalize(() => {
          this.refreshToken$ = null;
        })
      );

    return this.refreshToken$;
  }

  /**
   * Ensures a valid token exists for the given product. 
   * Checks expiration and refreshes if necessary.
   */
  ensureValidToken(productId: string): Observable<boolean> {
    const productName = this.getProductName(productId);
    const productConfig = this.getProductConfig(productName);

    if (!productConfig.requiresAccessToken) {
      return of(true);
    }

    const serverConfig = (environment as any).productServerUrls.find(
      (p: any) => p.name === productConfig.productServerName
    );
    const token = serverConfig?.accessTokenKey; // Note: In components this was sometimes accessing the actual token string if the interface on environment is different, but here assuming similar logic.
    // Wait, in components:
    // const serverConfig = environment.productServerUrls.find(...)
    // const token = serverConfig?.accessTokenKey; 
    // BUT later: const payload = JSON.parse(atob(token.split('.')[1])); 
    // This implies `serverConfig.accessTokenKey` HOLDS the token string? 
    // Or `serverConfig` is the config object and maybe `accessTokenKey` is the property name, but the code was doing `token.split` so `token` must be the JWT string.
    // Let's verify environment structure if possible, but following component logic:
    // The components treat `serverConfig?.accessTokenKey` as the JWT token string.

    // Actually, looking at `api.service.ts` or `environment.ts` would confirm if `accessTokenKey` is the token or the key to storage.
    // Component code: `const token = serverConfig?.accessTokenKey; ... JSON.parse(atob(token.split('.')[1]))`
    // This strongly suggests `accessTokenKey` IS the token value in the environment object (which seems odd for an environment file, maybe it's runtime updated or I'm misreading).
    // Ah, wait. `environment.productServerUrls` usually has `{ name: '...', url: '...', accessTokenKey: '...' }`.
    // If it's `accessTokenKey`, maybe it's the *key* in localStorage?
    // But `atob(token.split('.')[1])` implies `token` IS the JWT.
    // Let's copy the logic as is from the components since it was working there.
    // Wait, `MonthlyReport1Component` line 541: `const token = serverConfig?.accessTokenKey;`
    // If it is indeed the token, fine.

    if (!token) {
      return this.generateNewRefreshToken(productId).pipe(
        switchMap(() => of(true)),
        catchError(err => throwError(() => err))
      );
    }

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiry = payload.exp * 1000;
      const now = Date.now();
      const buffer = 5 * 60 * 1000; // 5 minutes

      if (expiry - now < buffer) {
        return this.generateNewRefreshToken(productId).pipe(
          switchMap(() => of(true)),
          catchError(err => throwError(() => err))
        );
      }
    } catch (e) {
      console.error('Error parsing token:', e);
      return this.generateNewRefreshToken(productId).pipe(
        switchMap(() => of(true)),
        catchError(err => throwError(() => err))
      );
    }

    return of(true);
  }
}
